import React, { useState, useCallback, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Play, RotateCcw, Download, AlertCircle, CheckCircle, Clock, Copy, Zap } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { OnboardingTestSuite, OnboardingTestSuiteRef } from "./OnboardingTestSuite";
import { AuthenticationTestSuite, AuthenticationTestSuiteRef } from "./AuthenticationTestSuite";
import { TestLogger } from "./TestLogger";
import { DatabaseInspector } from "./DatabaseInspector";
import { EdgeFunctionMonitor } from "./EdgeFunctionMonitor";
import { CallFlowTestSuite, CallFlowTestSuiteRef } from "./CallFlowTestSuite";
import { DNITestSuite, DNITestSuiteRef } from "./DNITestSuite";
import { toast } from "@/hooks/use-toast";

export interface TestResult {
  id: string;
  name?: string;
  testName: string;
  status: 'running' | 'passed' | 'failed' | 'pending' | 'success' | 'failure';
  startTime: Date;
  endTime?: Date;
  duration?: number;
  details?: string;
  errorMessage?: string;
  error?: string;
  debugData?: any;
  category?: string;
}

export interface TestLog {
  id: string;
  timestamp: Date;
  level: 'info' | 'warn' | 'error' | 'success' | 'debug';
  message: string;
  details?: any;
  testId?: string;
}

export const TestingComponent = () => {
  const { user, role, isAgency, isBusinessOwner } = useAuth();
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [testLogs, setTestLogs] = useState<TestLog[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [selectedTestSuite, setSelectedTestSuite] = useState<string>('authentication');
  const [runAllProgress, setRunAllProgress] = useState<{ current: number; total: number; suite: string } | null>(null);
  const loggerRef = useRef<any>(null);
  const authRef = useRef<AuthenticationTestSuiteRef | null>(null);
  const onboardingRef = useRef<OnboardingTestSuiteRef | null>(null);
  const callFlowRef = useRef<CallFlowTestSuiteRef | null>(null);
  const dniRef = useRef<DNITestSuiteRef | null>(null);
  const edgeFunctionRef = useRef<{ testAllFunctions: () => Promise<void> } | null>(null);

  // Load persisted test results on component mount
  useEffect(() => {
    loadPersistedTestResults();
  }, []);

  const loadPersistedTestResults = useCallback(() => {
    try {
      const savedResults = localStorage.getItem('cali-test-results');
      const savedLogs = localStorage.getItem('cali-test-logs');
      
      if (savedResults) {
        const parsedResults = JSON.parse(savedResults);
        const restoredResults = parsedResults.map((result: any) => ({
          ...result,
          startTime: new Date(result.startTime),
          endTime: result.endTime ? new Date(result.endTime) : undefined
        }));
        setTestResults(restoredResults);
        console.log('[TESTING] Loaded', restoredResults.length, 'persisted test results');
      }
      
      if (savedLogs) {
        const parsedLogs = JSON.parse(savedLogs);
        const restoredLogs = parsedLogs.map((log: any) => ({
          ...log,
          timestamp: new Date(log.timestamp)
        }));
        setTestLogs(restoredLogs);
        console.log('[TESTING] Loaded', restoredLogs.length, 'persisted test logs');
      }
    } catch (error) {
      console.error('[TESTING] Failed to load persisted test data:', error);
    }
  }, []);

  const persistTestData = useCallback((results: TestResult[], logs: TestLog[]) => {
    try {
      localStorage.setItem('cali-test-results', JSON.stringify(results));
      localStorage.setItem('cali-test-logs', JSON.stringify(logs));
      console.log('[TESTING] Persisted', results.length, 'test results and', logs.length, 'logs');
    } catch (error) {
      console.error('[TESTING] Failed to persist test data:', error);
    }
  }, []);

  const addTestResult = useCallback((result: TestResult) => {
    console.log('[TESTING] Adding test result:', result);
    
    const normalizedResult: TestResult = {
      ...result,
      testName: result.testName || result.name || 'Unknown Test',
      details: result.details || '',
      errorMessage: result.errorMessage || result.error,
      status: result.status as 'running' | 'passed' | 'failed' | 'pending' | 'success' | 'failure'
    };
    
    setTestResults(prev => {
      const existingIndex = prev.findIndex(r => r.id === result.id);
      let updatedResults;
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = normalizedResult;
        updatedResults = updated;
      } else {
        updatedResults = [...prev, normalizedResult];
      }
      
      setTimeout(() => persistTestData(updatedResults, testLogs), 100);
      return updatedResults;
    });
  }, [persistTestData, testLogs]);

  const addTestLog = useCallback((log: Omit<TestLog, 'id' | 'timestamp'>) => {
    const newLog: TestLog = {
      ...log,
      id: Math.random().toString(36).substr(2, 9),
      timestamp: new Date(),
    };
    setTestLogs(prev => {
      const updatedLogs = [...prev, newLog];
      setTimeout(() => persistTestData(testResults, updatedLogs), 100);
      return updatedLogs;
    });
    
    console.log(`[TEST-${log.level.toUpperCase()}]`, log.message, log.details || '');
  }, [persistTestData, testResults]);

  const clearResults = useCallback(() => {
    setTestResults([]);
    setTestLogs([]);
    localStorage.removeItem('cali-test-results');
    localStorage.removeItem('cali-test-logs');
    console.log('[TESTING] Cleared all test results and logs');
  }, []);

  const exportResults = useCallback(() => {
    const exportData = {
      timestamp: new Date().toISOString(),
      user: { id: user?.id, role, isAgency: isAgency(), isBusinessOwner: isBusinessOwner() },
      testResults,
      testLogs,
      summary: {
        total: testResults.length,
        passed: testResults.filter(r => r.status === 'passed' || r.status === 'success').length,
        failed: testResults.filter(r => r.status === 'failed' || r.status === 'failure').length,
        running: testResults.filter(r => r.status === 'running').length,
        pending: testResults.filter(r => r.status === 'pending').length,
      }
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `calie-test-results-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [testResults, testLogs, user, role, isAgency, isBusinessOwner]);

  /**
   * Format test results as readable text for clipboard
   */
  const formatResultsForClipboard = useCallback(() => {
    const passed = testResults.filter(r => r.status === 'passed' || r.status === 'success').length;
    const failed = testResults.filter(r => r.status === 'failed' || r.status === 'failure').length;
    const running = testResults.filter(r => r.status === 'running').length;

    let output = `=== Calie.ai Test Results ===\n`;
    output += `Date: ${new Date().toISOString()}\n`;
    output += `User: ${user?.email || 'Unknown'}\n`;
    output += `Role: ${role} | Type: ${isAgency() ? 'Agency' : isBusinessOwner() ? 'Business Owner' : 'Unknown'}\n\n`;
    output += `Summary: ${passed} passed, ${failed} failed, ${running} running\n`;
    output += `Total: ${testResults.length} tests\n\n`;

    // Group results by category
    const categories = new Map<string, TestResult[]>();
    testResults.forEach(result => {
      const category = result.category || 'General';
      if (!categories.has(category)) {
        categories.set(category, []);
      }
      categories.get(category)!.push(result);
    });

    categories.forEach((results, category) => {
      output += `--- ${category} Tests ---\n`;
      results.forEach(result => {
        const statusIcon = (result.status === 'passed' || result.status === 'success') ? '✅' : 
                          (result.status === 'failed' || result.status === 'failure') ? '❌' : '⏳';
        output += `${statusIcon} ${result.testName}: ${result.status}`;
        if (result.duration) output += ` (${result.duration}ms)`;
        output += '\n';
        if (result.details) output += `   Details: ${result.details}\n`;
        if (result.errorMessage) output += `   Error: ${result.errorMessage}\n`;
      });
      output += '\n';
    });

    // Add recent logs
    const recentLogs = testLogs.slice(-20);
    if (recentLogs.length > 0) {
      output += `--- Recent Logs (last ${recentLogs.length}) ---\n`;
      recentLogs.forEach(log => {
        const levelIcon = log.level === 'error' ? '🔴' : 
                         log.level === 'warn' ? '🟡' : 
                         log.level === 'success' ? '🟢' : '🔵';
        output += `${levelIcon} [${log.level.toUpperCase()}] ${log.message}\n`;
      });
    }

    return output;
  }, [testResults, testLogs, user, role, isAgency, isBusinessOwner]);

  /**
   * Copy results to clipboard
   */
  const copyResults = useCallback(async () => {
    try {
      const text = formatResultsForClipboard();
      await navigator.clipboard.writeText(text);
      toast({
        title: "Results copied",
        description: "Test results copied to clipboard in readable format",
      });
    } catch (error) {
      console.error('[TESTING] Failed to copy results:', error);
      toast({
        title: "Copy failed",
        description: "Could not copy results to clipboard",
        variant: "destructive",
      });
    }
  }, [formatResultsForClipboard]);

  /**
   * Run all test suites comprehensively
   */
  const runAllTestSuites = useCallback(async () => {
    setIsRunning(true);
    addTestLog({ level: 'info', message: '🚀 Starting full test suite run across all 5 suites...' });

    const suites = [
      { name: 'Authentication', ref: authRef, tab: 'authentication' },
      { name: 'Onboarding', ref: onboardingRef, tab: 'onboarding' },
      { name: 'Call Flow', ref: callFlowRef, tab: 'call-flow' },
      { name: 'DNI', ref: dniRef, tab: 'dni' },
      { name: 'Edge Functions', ref: edgeFunctionRef, tab: 'functions' },
    ];

    try {
      for (let i = 0; i < suites.length; i++) {
        const suite = suites[i];
        setRunAllProgress({ current: i + 1, total: suites.length, suite: suite.name });
        setSelectedTestSuite(suite.tab);
        addTestLog({ level: 'info', message: `Running ${suite.name} tests... (${i + 1}/${suites.length})` });
        
        await new Promise(resolve => setTimeout(resolve, 500));
        
        if (suite.name === 'Edge Functions' && edgeFunctionRef.current) {
          await edgeFunctionRef.current.testAllFunctions();
        } else if (suite.ref.current && 'runAllTests' in suite.ref.current) {
          await (suite.ref.current as { runAllTests: () => Promise<void> }).runAllTests();
        } else {
          addTestLog({ level: 'warn', message: `${suite.name} suite ref not available, skipping...` });
        }
        
        // Brief pause between suites
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      addTestLog({ level: 'success', message: '✅ Full test suite completed across all 5 suites!' });
      toast({
        title: "All tests completed",
        description: `Ran ${suites.length} test suites`,
      });
    } catch (error) {
      addTestLog({ level: 'error', message: `Test suite run failed: ${error}` });
    } finally {
      setIsRunning(false);
      setRunAllProgress(null);
    }
  }, [addTestLog]);

  const getStatusIcon = (status: TestResult['status']) => {
    switch (status) {
      case 'passed':
      case 'success': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'failed':
      case 'failure': return <AlertCircle className="h-4 w-4 text-destructive" />;
      case 'running': return <Clock className="h-4 w-4 text-primary animate-spin" />;
      default: return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusColor = (status: TestResult['status']) => {
    switch (status) {
      case 'passed':
      case 'success': return 'bg-green-600 text-white';
      case 'failed':
      case 'failure': return 'bg-destructive text-destructive-foreground';
      case 'running': return 'bg-primary text-primary-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const testSummary = {
    total: testResults.length,
    passed: testResults.filter(r => r.status === 'passed' || r.status === 'success').length,
    failed: testResults.filter(r => r.status === 'failed' || r.status === 'failure').length,
    running: testResults.filter(r => r.status === 'running').length,
  };

  const progressPercent = runAllProgress 
    ? Math.round((runAllProgress.current / runAllProgress.total) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header with controls */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Test Control Panel</CardTitle>
              <CardDescription>
                Current User: {user?.email} | Role: {role} | 
                Type: {isAgency() ? 'Agency' : isBusinessOwner() ? 'Business Owner' : 'Unknown'}
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={runAllTestSuites}
                disabled={isRunning}
                className="bg-primary"
              >
                <Zap className="h-4 w-4 mr-2" />
                Run All Tests
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={copyResults}
                disabled={testResults.length === 0}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Results
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={clearResults}
                disabled={isRunning}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Clear
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={exportResults}
                disabled={testResults.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Progress bar when running all tests */}
          {runAllProgress && (
            <div className="mb-4 p-3 bg-primary/10 border border-primary/20 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-primary">
                  Running: {runAllProgress.suite}
                </span>
                <span className="text-sm text-muted-foreground">
                  {runAllProgress.current}/{runAllProgress.total} suites
                </span>
              </div>
              <Progress value={progressPercent} className="h-2" />
            </div>
          )}

          <div className="grid grid-cols-5 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">{testSummary.total}</div>
              <div className="text-sm text-muted-foreground">Total Tests</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{testSummary.passed}</div>
              <div className="text-sm text-muted-foreground">Passed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-destructive">{testSummary.failed}</div>
              <div className="text-sm text-muted-foreground">Failed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">{testSummary.running}</div>
              <div className="text-sm text-muted-foreground">Running</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {testResults.length > 0 ? '✓' : '○'}
              </div>
              <div className="text-sm text-muted-foreground">Persisted</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main testing interface */}
      <Tabs value={selectedTestSuite} onValueChange={setSelectedTestSuite} className="space-y-4">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="authentication">Auth</TabsTrigger>
          <TabsTrigger value="onboarding">Onboarding</TabsTrigger>
          <TabsTrigger value="call-flow">Call Flow</TabsTrigger>
          <TabsTrigger value="dni">DNI</TabsTrigger>
          <TabsTrigger value="database">Database</TabsTrigger>
          <TabsTrigger value="functions">Functions</TabsTrigger>
          <TabsTrigger value="logs">Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="authentication" className="space-y-4">
          <AuthenticationTestSuite
            ref={authRef}
            onTestResult={addTestResult}
            onTestLog={addTestLog}
            isRunning={isRunning}
            setIsRunning={setIsRunning}
          />
        </TabsContent>

        <TabsContent value="onboarding" className="space-y-4">
          <OnboardingTestSuite
            ref={onboardingRef}
            onTestResult={addTestResult}
            onTestLog={addTestLog}
            isRunning={isRunning}
            setIsRunning={setIsRunning}
          />
        </TabsContent>

        <TabsContent value="database" className="space-y-4">
          <DatabaseInspector onTestLog={addTestLog} />
        </TabsContent>

        <TabsContent value="functions" className="space-y-4">
          <EdgeFunctionMonitor
            ref={edgeFunctionRef}
            onTestResult={addTestResult}
            onTestLog={addTestLog}
          />
        </TabsContent>

        <TabsContent value="call-flow" className="space-y-4">
          <CallFlowTestSuite
            ref={callFlowRef}
            onTestResult={addTestResult}
            onTestLog={addTestLog}
            isRunning={isRunning}
            setIsRunning={setIsRunning}
          />
        </TabsContent>

        <TabsContent value="dni" className="space-y-4">
          <DNITestSuite
            ref={dniRef}
            onTestResult={addTestResult}
            onTestLog={addTestLog}
            isRunning={isRunning}
            setIsRunning={setIsRunning}
          />
        </TabsContent>

        <TabsContent value="logs" className="space-y-4">
          <TestLogger ref={loggerRef} logs={testLogs} onClearLogs={() => setTestLogs([])} />
        </TabsContent>
      </Tabs>

      {/* Test Results */}
      {testResults.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Test Results</CardTitle>
                <CardDescription>Real-time test execution results with detailed debugging information</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={copyResults}>
                <Copy className="h-4 w-4 mr-2" />
                Copy All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-96">
              <div className="space-y-3">
                {testResults.map((result) => (
                  <div key={result.id} className="flex items-start gap-3 p-3 border border-border rounded-lg">
                    <div className="flex-shrink-0 mt-0.5">
                      {getStatusIcon(result.status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="font-medium text-foreground">{result.testName}</h4>
                        <Badge variant="outline" className={`${getStatusColor(result.status)}`}>
                          {result.status}
                        </Badge>
                        {result.category && (
                          <Badge variant="secondary" className="text-xs">
                            {result.category}
                          </Badge>
                        )}
                        {result.duration && (
                          <span className="text-sm text-muted-foreground">
                            {result.duration}ms
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{result.details}</p>
                      {result.errorMessage && (
                        <div className="bg-destructive/10 border border-destructive/20 rounded p-2 text-sm text-destructive">
                          <strong>Error:</strong> {result.errorMessage}
                        </div>
                      )}
                      {result.debugData && (
                        <details className="mt-2">
                          <summary className="text-sm font-medium cursor-pointer hover:text-primary">
                            Debug Data
                          </summary>
                          <pre className="mt-1 p-2 bg-muted rounded text-xs overflow-auto">
                            {JSON.stringify(result.debugData, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
